import { ChevronRight, Dots, Mail, Plus, SendIcon } from 'assets/icons';
import Button from 'components/button';
import { cardIconButtonClass } from 'components/card/PanelCard';
import MenuButton from 'components/DropdownMenu/MenuButton';
import CompactEmptyState from 'components/EmptyState/CompactEmptyState';
import IconTooltip from 'components/iconTooltip/IconTooltip';
import { companyRoleLabel } from 'constants/companyRoles';
import useText from 'hooks/useText';
import { useState } from 'react';
import { LIST_GROUP_HEADER_CLASS } from 'utils/surfaceClasses';
import ContactRow from './ContactRow';
import MemberRoleDialog from './MemberRoleDialog';
import { RecordSection } from './RecordLayout';

// The header of a group of contacts, across the whole card as the groups of
// the home page's tasks: the first one takes the card's top corners.
const GroupHead = ({ first, children }) => (
  <p className={`-mx-4 border-b border-gray-100 ${LIST_GROUP_HEADER_CLASS} ${first ? '-mt-4 rounded-t-xl' : 'border-t'}`}>{children}</p>
);

/**
 * The « Contacts » of a company, as the admin's tab: its members registered
 * on Jobbiz (an invitation pending can be sent again), then the people of
 * HubSpot without an account (they are invited); each opens their page. Then
 * the contacts of its group, read only: they are managed on the group.
 * Without `onAdd` and `onEdit`, its own contacts are read only; without
 * `onInvite` (a prospect, JBWEB-1424), no « Inviter ». With `onRoleChanged`,
 * the « ⋯ » of a member changes their Jobbiz role in `companyId`, then calls it.
 * A position not given reads « - ».
 */
const CompanyContacts = ({
  companyId,
  members,
  hubspot,
  group,
  groupContacts,
  onOpen,
  onEdit,
  onAdd,
  onInvite,
  onResend,
  onRoleChanged,
  onOpenGroup,
  onOpenGroupContact,
}) => {
  const { text, language } = useText('record');
  const [roleOf, setRoleOf] = useState(null);
  const detail = (person) =>
    [
      person.position || '-',
      person.account === 'active' && person.role && text('access', { role: companyRoleLabel(language, person.role) }),
      person.phone,
      person.email,
    ]
      .filter(Boolean)
      .join(' · ');
  const count = members.length + hubspot.length;
  return (
    <div className="flex flex-col gap-4 pb-6">
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <p className="text-sm font-medium text-gray-900">{text('contactsCount', { count })}</p>
          {onAdd && (
            <Button color="SECONDARY" size="SM" onClick={onAdd}>
              <Plus wh={18} color="currentColor" />
              <span>{text('addContact')}</span>
            </Button>
          )}
        </div>
        <RecordSection>
          {!count && <CompactEmptyState compact glyph="inbox" title={text('contactsEmptyTitle')} description={text('contactsEmptyText')} />}
          {members.length > 0 && (
            <div>
              <GroupHead first>{text('registered', { count: members.length })}</GroupHead>
              {members.map((person) => (
                <ContactRow
                  key={person.id}
                  person={person}
                  detail={detail(person)}
                  onOpen={() => onOpen(person)}
                  actions={
                    <>
                      {person.account === 'pending' && (
                        <IconTooltip label={text('resend')}>
                          <button type="button" aria-label={text('resend')} onClick={() => onResend(person)} className={cardIconButtonClass}>
                            <Mail wh={18} color="#374151" />
                          </button>
                        </IconTooltip>
                      )}
                      {onRoleChanged && (
                        <MenuButton
                          trigger={(toggleMenu) => (
                            <button
                              type="button"
                              aria-label={text('memberActions', { name: [person.firstname, person.lastname].filter(Boolean).join(' ') })}
                              onClick={toggleMenu}
                              className={cardIconButtonClass}
                            >
                              <Dots w={16} h={4} color="#374151" />
                            </button>
                          )}
                          items={[{ label: text('changeRole'), onClick: () => setRoleOf(person) }]}
                        />
                      )}
                    </>
                  }
                />
              ))}
            </div>
          )}
          {hubspot.length > 0 && (
            <div>
              <GroupHead first={!members.length}>{text('notRegistered', { count: hubspot.length })}</GroupHead>
              {hubspot.map((person) => (
                <ContactRow
                  key={person.id}
                  person={person}
                  detail={detail(person)}
                  // A contact without an account of the company (source 'crm') opens its modal.
                  onOpen={person.source === 'crm' ? onEdit && (() => onEdit(person)) : () => onOpen(person)}
                  actions={
                    onInvite && (
                      <Button color="SECONDARY" size="SM" onClick={() => onInvite(person)}>
                        <SendIcon wh={16} color="currentColor" />
                        <span>{text('invite')}</span>
                      </Button>
                    )
                  }
                />
              ))}
            </div>
          )}
        </RecordSection>
      </div>
      {roleOf && (
        <MemberRoleDialog
          companyId={companyId}
          member={roleOf}
          onClose={() => setRoleOf(null)}
          onSaved={() => {
            setRoleOf(null);
            onRoleChanged();
          }}
        />
      )}
      {group && (
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-gray-900">{text('groupContacts', { count: groupContacts.length })}</p>
            <Button color="SECONDARY" size="SM" onClick={onOpenGroup}>
              <span>{group.name}</span>
              <ChevronRight wh={16} color="currentColor" />
            </Button>
          </div>
          <RecordSection>
            {!groupContacts.length && <p className="text-sm text-gray-600">{text('groupContactsEmpty')}</p>}
            {groupContacts.map((person) => (
              <ContactRow
                key={person.id}
                person={person}
                detail={[person.position, person.phone, person.email].filter(Boolean).join(' · ')}
                onOpen={() => onOpenGroupContact(person)}
              />
            ))}
          </RecordSection>
        </div>
      )}
    </div>
  );
};

export default CompanyContacts;
